import { ThemeEvents } from '@theme/events';

/**
 * <cart-upsells>
 *
 * Shows "You Might Like" products in the cart drawer. The Liquid renders the
 * merchant's fallback collection on first paint; this element then asks the
 * Recommendations API for products related to the most recently added product
 * and swaps the list in. If the API returns nothing, the fallback list stays.
 *
 * The recommendations endpoint re-renders the host (header) section with the
 * `recommendations` object populated, so we fetch it, pull out the freshly
 * rendered <cart-upsells> subtree, and replace our list with its list.
 */
class CartUpsells extends HTMLElement {
  /**
   * Product id we last loaded recommendations for, to avoid duplicate fetches.
   * @type {string | null}
   */
  #loadedFor = null;

  /**
   * Product id of the most recently added item. Preferred over `data-product-id`
   * (which the server sets to `cart.items.last` — not the newest add when that
   * product was already in the cart and its line quantity bumped in place).
   * @type {string | null}
   */
  #lastAddedProductId = null;

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#load();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#activeFetch?.abort();
  }

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    // Prefer the product just added (a CartAddEvent carries it) so the
    // recommendations reflect the MOST RECENTLY ADDED product.
    const addedId = /** @type {any} */ (event)?.detail?.data?.productId;
    if (addedId) this.#lastAddedProductId = String(addedId);

    // On every cart change the drawer/cart section is morphed: our previously
    // swapped-in recommendations are reverted to the server fallback list and
    // `data-product-id` is updated to the new last cart item. So we must always
    // re-hydrate (clear the dedupe guard), and we defer to a microtask so the
    // load runs AFTER the morph has applied — otherwise, depending on listener
    // order within this same event dispatch, we could read a stale product id
    // and swap nothing in, leaving the fallback visible until a page reload.
    this.#loadedFor = null;
    queueMicrotask(() => this.#load());
  };

  async #load() {
    const productId = this.#lastAddedProductId ?? this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    const url = this.dataset.url;

    if (!productId || !sectionId || !url) return;
    if (this.#loadedFor === productId) return;
    this.#loadedFor = productId;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(
        `${url}&product_id=${productId}&section_id=${sectionId}`,
        { signal: this.#activeFetch.signal }
      );
      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshList = doc.querySelector('cart-upsells .cart-upsells__list');

      // No recommendations came back — keep the fallback list already shown.
      if (!freshList) return;

      const currentList = this.querySelector('.cart-upsells__list');
      if (currentList) {
        currentList.replaceWith(freshList);
      } else {
        // We were empty (no fallback). Adopt the whole fresh subtree's children.
        const freshRoot = doc.querySelector('cart-upsells');
        if (freshRoot) this.innerHTML = freshRoot.innerHTML;
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        // Non-fatal: the fallback collection (if any) remains visible.
        this.#loadedFor = null;
      }
    } finally {
      this.#activeFetch = null;
    }
  }
}

if (!customElements.get('cart-upsells')) {
  customElements.define('cart-upsells', CartUpsells);
}
